/**
 * A thin, safe wrapper over gtag.
 *
 * GA4 is optional on this site - the tag only loads when a Measurement ID is
 * filled in at Admin -> Settings - so every function here must be a silent
 * no-op when gtag is absent, rather than throwing and taking the calling
 * component down with it. An analytics bug must never break the calculator.
 *
 * Two GA4 limits shape this file:
 *   - a property allows 500 distinct event NAMES, so anything that varies
 *     (a route, an expressway, a question) belongs in a parameter, never in
 *     the name;
 *   - parameter values are cut at 100 characters, so truncate here and mark
 *     it rather than letting GA4 silently lose the tail.
 */

const MAX_PARAM = 100;

/**
 * Drop empty values and clamp long strings. GA4 accepts nulls but then shows
 * "(not set)" in reports, which is indistinguishable from a bug.
 */
function scrub(params) {
    const out = {};

    for (const [key, value] of Object.entries(params || {})) {
        if (value === undefined || value === null || value === '') continue;

        out[key] = typeof value === 'string' && value.length > MAX_PARAM
            ? value.slice(0, MAX_PARAM - 1) + '\u2026'
            : value;
    }

    return out;
}

export function track(name, params = {}) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    try {
        window.gtag('event', name, scrub(params));
    } catch {
        /* never let measurement break the page */
    }
}

const fired = new Set();

/** Milestones that should count once per page view, e.g. scroll depth. */
export function trackOnce(key, name, params = {}) {
    if (fired.has(key)) return;
    fired.add(key);
    track(name, params);
}

/**
 * Collapses a burst of typing into one event once the user pauses. The fuel
 * calculator has no submit button - it recomputes on every keystroke - so
 * without this one estimate would file a dozen events.
 */
export function debounced(wait = 1200) {
    let timer = null;

    return (name, params = {}) => {
        clearTimeout(timer);
        timer = setTimeout(() => track(name, params), wait);
    };
}
